/* eslint-disable react/prop-types */
import React from 'react'
import * as d3 from 'd3'
import { hexbin } from 'd3-hexbin'
import { max } from 'd3-array'

import RunCalculations, {
    Calculations,
} from '../../Utilities/RunCalculations.ts'
import ColorPalette from '../../Utilities/Colors/Palettes.ts'

import HexLegend from '../../Legend/HexLegend.tsx'
import type { EVPData } from '../../schema/types.ts'
import {
    useChartOptions,
    usePixelPositions,
} from '../../Context/ChartContext.tsx'

export type DataWithPixels = EVPData & { px: number; py: number }

export default function HexBin() {
    const { settings, chartOptions } = useChartOptions()
    const pitchData = settings.data
    // TODO: Can we do better?
    const chartType = chartOptions.type as keyof Calculations

    const pixelPositions = usePixelPositions()

    const dataWithPixels = pitchData.map((d, i) => ({
        ...d,
        px: pixelPositions[i].x,
        py: pixelPositions[i].y,
    }))

    const radiusSize = 14

    const hex = hexbin<DataWithPixels>()
        .x((d) => d.px)
        .y((d) => d.py)
        .radius((radiusSize * settings.vizWidth) / settings.vizWidth - 1)
        .extent([
            [0, 0],
            [settings.vizWidth - 0, settings.vizHeight - 0],
        ])

    const bins = hex(dataWithPixels)

    const binsWithCalcs = bins.map((d) => ({
        data: d,
        calculations: RunCalculations(d, d.length),
    }))

    const palette = new ColorPalette({
        type: chartType,
        data: binsWithCalcs,
    })
    const { color } = palette

    const maxCount = max(bins, (d) => d.length)

    if (!maxCount) {
        return null
    }

    let radius: (length: number) => number = d3.scaleSqrt(
        [0, maxCount * 1.8],
        [0, hex.radius() * Math.SQRT2]
    )

    if (!chartOptions.scaleRadius) {
        radius = () => radiusSize
    }
    return (
        <>
            <g id="hexbinChart">
                {binsWithCalcs.map((d, i) => {
                    const fill = String(
                        color(Number(d.calculations[chartType])) || '#000'
                    )
                    return (
                        <path
                            // eslint-disable-next-line react/no-array-index-key
                            key={`hexPath_${i}`}
                            d={hex.hexagon(radius(d.data.length))}
                            transform={`translate(${d.data.x},${d.data.y})`}
                            fill={fill}
                            stroke="#000"
                            opacity={chartOptions.opacity}
                        />
                    )
                })}
            </g>
            {chartOptions.legend ? (
                <HexLegend
                    settings={settings}
                    color={color}
                    radius={radius}
                    maxCount={maxCount}
                    chartOptions={chartOptions}
                    hex={hex}
                />
            ) : null}
        </>
    )
}
